/**
 * Schedule Calculator - Shared module for calculating reminder occurrences
 * 
 * This module is used by both frontend (UI preview) and backend (notification scheduling)
 * 
 * Usage:
 * - Frontend: import from 'lib/shared/schedule-calculator.js' or '@/lib/shared/schedule-calculator'
 * - Backend: import from './lib/shared/schedule-calculator.js'
 */

/**
 * Available recurrence types (biweekly and custom removed)
 */
export const RECURRENCE_TYPES = ['once', 'daily', 'weekly', 'monthly', 'quarterly', 'yearly'];

/**
 * Default weekdays (Monday to Friday)
 */
export const DEFAULT_WEEKDAYS = [1, 2, 3, 4, 5]; // Mon, Tue, Wed, Thu, Fri

/**
 * Days of week configuration
 */
export const DAYS_OF_WEEK = [
    { value: 0, label: 'Sun', labelKey: 'sunday' },
    { value: 1, label: 'Mon', labelKey: 'monday' },
    { value: 2, label: 'Tue', labelKey: 'tuesday' },
    { value: 3, label: 'Wed', labelKey: 'wednesday' },
    { value: 4, label: 'Thu', labelKey: 'thursday' },
    { value: 5, label: 'Fri', labelKey: 'friday' },
    { value: 6, label: 'Sat', labelKey: 'saturday' }
];

/**
 * Get time components in a specific timezone
 * @param {Date} date - The date to convert
 * @param {string} timezone - Target timezone (e.g., 'Asia/Taipei')
 * @returns {Object} - { hour, minute, dayOfWeek, dayOfMonth, month, year }
 */
export function getTimeInTimezone(date, timezone = 'Asia/Shanghai') {
    const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: timezone,
        hour: 'numeric',
        minute: 'numeric',
        hour12: false,
        weekday: 'short',
        day: 'numeric',
        month: 'numeric',
        year: 'numeric'
    });
    
    const parts = formatter.formatToParts(date);
    const result = {};
    parts.forEach(part => {
        if (part.type === 'hour') result.hour = parseInt(part.value);
        if (part.type === 'minute') result.minute = parseInt(part.value);
        if (part.type === 'weekday') result.weekday = part.value;
        if (part.type === 'day') result.dayOfMonth = parseInt(part.value);
        if (part.type === 'month') result.month = parseInt(part.value);
        if (part.type === 'year') result.year = parseInt(part.value);
    });
    
    // Map weekday to number
    const weekdayMap = { 'Sun': 0, 'Mon': 1, 'Tue': 2, 'Wed': 3, 'Thu': 4, 'Fri': 5, 'Sat': 6 };
    result.dayOfWeek = weekdayMap[result.weekday];
    
    return result;
}

/**
 * Check if a date falls on a weekday
 * @param {Date} date - The date to check
 * @param {string} timezone - Timezone to use
 * @returns {boolean}
 */
export function isWeekday(date, timezone = 'Asia/Shanghai') {
    const { dayOfWeek } = getTimeInTimezone(date, timezone);
    return dayOfWeek >= 1 && dayOfWeek <= 5;
}

/**
 * Find the next weekday from a given date
 * @param {Date} date - Starting date
 * @param {string} timezone - Timezone to use
 * @returns {Date}
 */
export function getNextWeekday(date, timezone = 'Asia/Shanghai') {
    const result = new Date(date);
    while (!isWeekday(result, timezone)) {
        result.setDate(result.getDate() + 1);
    }
    return result;
}

/**
 * Calculate the next occurrence for a reminder schedule
 * Uses startDate (date) + time (HH:MM) to calculate exact reminder time
 * @param {Object} schedule - Schedule configuration { startDate, time, timezone, recurrence, endDate, nextOccurrence }
 * @param {Date} fromDate - Calculate from this date (defaults to now)
 * @returns {Date|null} - Next occurrence date or null if no more occurrences
 */
export function calculateNextOccurrence(schedule, fromDate = null) {
    if (!schedule || !schedule.recurrence) return null;
    
    const now = fromDate || new Date();
    const endDate = schedule.endDate ? new Date(schedule.endDate) : null;
    const recurrence = schedule.recurrence;
    const timezone = schedule.timezone || 'Asia/Shanghai';
    
    // If end date passed, no next occurrence
    if (endDate && now > endDate) return null;
    
    // Parse time from schedule.time (HH:MM format)
    const [hours, minutes] = (schedule.time || '09:00').split(':').map(Number);
    
    // For 'once' type, no next occurrence after first trigger
    if (recurrence.type === 'once' || recurrence.interval === 0) return null;
    
    // Start from current occurrence or start date
    let nextDate = new Date(schedule.nextOccurrence || schedule.startDate);
    const interval = recurrence.interval || 1;
    
    switch (recurrence.type) {
        case 'daily': {
            nextDate.setDate(nextDate.getDate() + interval);
            // If daysOfWeek specified, find next matching day
            if (recurrence.daysOfWeek?.length > 0) {
                while (!recurrence.daysOfWeek.includes(nextDate.getDay())) {
                    nextDate.setDate(nextDate.getDate() + 1);
                }
            }
            break;
        }
        
        case 'weekly': {
            // If specific days of week are set
            if (recurrence.daysOfWeek?.length > 0) {
                nextDate = findNextDayOfWeek(nextDate, recurrence.daysOfWeek, interval, timezone);
            } else {
                nextDate.setDate(nextDate.getDate() + interval * 7);
            }
            break;
        }
        
        case 'monthly': {
            // Support multiple days of month (daysOfMonth array)
            const daysOfMonth = recurrence.daysOfMonth || [1];
            nextDate = findNextDayOfMonth(nextDate, daysOfMonth, interval);
            break;
        }
        
        case 'quarterly': {
            const daysOfMonth = recurrence.daysOfMonth || [1];
            nextDate = findNextDayOfMonth(nextDate, daysOfMonth, interval * 3);
            break;
        }
        
        case 'yearly': {
            const daysOfMonth = recurrence.daysOfMonth || [1];
            nextDate.setFullYear(nextDate.getFullYear() + interval);
            // Find next matching day in the new year
            const sortedDays = [...daysOfMonth].sort((a, b) => a - b);
            const currentDay = nextDate.getDate();
            const nextDay = sortedDays.find(d => d > currentDay) || sortedDays[0];
            if (nextDay < currentDay) {
                nextDate.setFullYear(nextDate.getFullYear() + interval);
            }
            nextDate.setDate(nextDay);
            break;
        }
        
        default:
            return null;
    }
    
    // Apply the time component
    nextDate.setHours(hours, minutes, 0, 0);
    
    // Check if next occurrence is past end date
    if (endDate && nextDate > endDate) return null;
    
    return nextDate;
}

/**
 * Find the next day that matches one of the specified days of week
 * @param {Date} fromDate - Starting date
 * @param {number[]} daysOfWeek - Array of day numbers (0=Sun, 1=Mon, etc.)
 * @param {number} weekInterval - Number of weeks to skip between occurrences
 * @param {string} timezone - Timezone to use
 * @returns {Date}
 */
function findNextDayOfWeek(fromDate, daysOfWeek, weekInterval = 1, timezone = 'Asia/Shanghai') {
    if (!daysOfWeek?.length) return fromDate;
    
    const result = new Date(fromDate);
    result.setDate(result.getDate() + 1); // Start from next day
    
    const sortedDays = [...daysOfWeek].sort((a, b) => a - b);
    const currentDayOfWeek = getTimeInTimezone(fromDate, timezone).dayOfWeek;
    
    // Find next day in current week
    for (const day of sortedDays) {
        if (day > currentDayOfWeek) {
            result.setDate(fromDate.getDate() + (day - currentDayOfWeek));
            return result;
        }
    }
    
    // No more days in current week, go to next week (or skip weeks based on interval)
    const daysUntilNextWeek = 7 - currentDayOfWeek + sortedDays[0];
    const extraWeeks = (weekInterval - 1) * 7;
    result.setDate(fromDate.getDate() + daysUntilNextWeek + extraWeeks);
    
    return result;
}

/**
 * Find the next day of month that matches one of the specified days
 * @param {Date} fromDate - Starting date (should have time already set)
 * @param {number[]} daysOfMonth - Array of day numbers (1-31)
 * @param {number} monthsToAdd - Number of months to add (1 for monthly, 3 for quarterly)
 * @returns {Date}
 */
function findNextDayOfMonth(fromDate, daysOfMonth, monthsToAdd = 1) {
    if (!daysOfMonth?.length) return fromDate;
    
    const result = new Date(fromDate);
    const sortedDays = [...daysOfMonth].sort((a, b) => a - b);
    const currentDay = result.getDate();
    
    // Try to find a day in the current month that's after fromDate
    const nextDayInMonth = sortedDays.find(d => {
        if (d <= currentDay) return false;
        const testDate = new Date(result);
        testDate.setDate(d);
        return testDate > fromDate;
    });
    
    if (nextDayInMonth) {
        // Found a day in current month that's after fromDate
        result.setDate(nextDayInMonth);
    } else {
        // No more days in current month, move to next period
        result.setMonth(result.getMonth() + monthsToAdd);
        result.setDate(sortedDays[0]);
    }
    
    return result;
}

/**
 * Calculate multiple future occurrences for preview
 * @param {Object} schedule - Schedule configuration { startDate, time, timezone, recurrence, endDate }
 * @param {number} count - Number of occurrences to calculate
 * @returns {Date[]} - Array of future occurrence dates
 */
export function calculateFutureOccurrences(schedule, count = 5) {
    if (!schedule || !schedule.recurrence) return [];
    
    const now = new Date();
    
    // Parse time from schedule.time (HH:MM format)
    const [hours, minutes] = (schedule.time || '09:00').split(':').map(Number);
    
    // For 'once' type, return the start date with time applied (with optional daysOfWeek filter)
    if (schedule.recurrence.type === 'once' || schedule.recurrence.interval === 0) {
        const startDate = new Date(schedule.startDate);
        startDate.setHours(hours, minutes, 0, 0);
        
        // Check if daysOfWeek filter is specified
        if (schedule.recurrence.daysOfWeek?.length > 0) {
            // Find next matching day
            let checkDate = new Date(startDate);
            while (!schedule.recurrence.daysOfWeek.includes(checkDate.getDay()) || checkDate <= now) {
                checkDate.setDate(checkDate.getDate() + 1);
            }
            checkDate.setHours(hours, minutes, 0, 0);
            return [checkDate];
        }
        
        // Check if startDate is in the future
        if (startDate > now) {
            return [startDate];
        } else {
            return []; // Past time, no occurrences
        }
    }
    
    const occurrences = [];
    let currentSchedule = {
        ...schedule,
        nextOccurrence: schedule.startDate
    };
    
    // Add the first occurrence (startDate + time) only if it's in the future
    const firstOccurrence = new Date(schedule.startDate);
    firstOccurrence.setHours(hours, minutes, 0, 0);
    
    // For daily with daysOfWeek, check if first occurrence matches
    if (schedule.recurrence.type === 'daily' && schedule.recurrence.daysOfWeek?.length > 0) {
        if (schedule.recurrence.daysOfWeek.includes(firstOccurrence.getDay()) && firstOccurrence > now) {
            occurrences.push(firstOccurrence);
        }
    } else if (firstOccurrence > now) {
        occurrences.push(firstOccurrence);
    }
    
    // Calculate subsequent occurrences
    while (occurrences.length < count) {
        const next = calculateNextOccurrence(currentSchedule);
        if (!next) break;
        
        // Only add if in the future
        if (next > now) {
            occurrences.push(next);
        }
        
        currentSchedule = {
            ...currentSchedule,
            nextOccurrence: next.toISOString()
        };
    }
    
    return occurrences;
}

/**
 * Format a date for display
 * @param {Date} date - Date to format
 * @param {string} timezone - Timezone to use
 * @param {string} locale - Locale for formatting
 * @returns {string}
 */
export function formatOccurrence(date, timezone = 'Asia/Shanghai', locale = 'zh-TW') {
    return new Intl.DateTimeFormat(locale, {
        timeZone: timezone,
        weekday: 'short',
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    }).format(date);
}

/**
 * Create default schedule with workday defaults
 * @returns {Object}
 */
export function createDefaultSchedule() {
    const now = new Date();
    const tomorrow = new Date(now);
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(9, 0, 0, 0);
    
    return {
        startDate: tomorrow.toISOString().slice(0, 16),
        endDate: '',
        time: '09:00',
        timezone: 'Asia/Shanghai',
        recurrence: {
            type: 'weekly', // Default to weekly with workdays
            interval: 1,
            daysOfWeek: DEFAULT_WEEKDAYS, // Default to workdays (Mon-Fri)
            daysOfMonth: [1] // For monthly/quarterly/yearly
        }
    };
}

export default {
    RECURRENCE_TYPES,
    DEFAULT_WEEKDAYS,
    DAYS_OF_WEEK,
    getTimeInTimezone,
    isWeekday,
    getNextWeekday,
    calculateNextOccurrence,
    calculateFutureOccurrences,
    formatOccurrence,
    createDefaultSchedule
};
